// JamControl.js — Jam Control Station.
// Per-minute receive production (MonitorPortal, MP_MINUTES_<date>/<shift>) for a
// shift, with AM-drawn flags over minute ranges (Jams, AGL, Door Closures,
// e-stop, SEV). Flags are an append-only event log on the existing
// fclm/scan-append endpoint (atomic list_append): many AMs can flag at once,
// nothing is overwritten or deleted — "remove" appends a tombstone.
// OLM1 also gets JamBot auto flags (backend/scada_jam_sync.py, SCADA alarms ->
// JAMCTRL_AUTO_<date>): Jams, AGL (full beds) and Downstream Unavailable. An auto
// flag is drawn on the chart (and counts toward Lost Cartons) only if production
// dipped: the avg over its minutes — at least 3, from its start — is below half the
// hour's avg outside all flags. Otherwise it is "count only": listed and counted, not
// drawn. AMs can remove auto flags like any other (tombstone in the AM log).
// JamBot AGL (Full Alarm) events are too frequent for this view: they are left out
// here and shown per bed on the Sorter Map tab instead. AM-drawn AGL flags stay.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const FLAG_TYPES = [
  { key: 'Jams', color: '#DE1B1B' },
  { key: 'AGL', color: '#FF8F00' },
  { key: 'Door Closures', color: '#077398' },
  { key: 'e-stop', color: '#8169F8' },
  { key: 'SEV', color: '#0B0C0C' },
  { key: 'Downstream Unavailable', color: '#C2185B' },
];
// Auto flag on the chart only if the window avg is below this share of the hour's clean avg.
const DIP_RATIO = 0.5;
const DIP_MIN_WINDOW = 3;
// JamBot event types not shown here (AGL lives on the Sorter Map tab).
const AUTO_HIDDEN = new Set(['AGL']);
const COLOR = Object.fromEntries(FLAG_TYPES.map(t => [t.key, t.color]));
const SERIES = { pid: 'PID', aros: 'AROS', both: 'PID + AROS' };
// Shift windows (site-local), minutes from midnight; end may pass midnight.
const SHIFTS = {
  OLM1: { day: [8 * 60, 18 * 60 + 30], night: [19 * 60, 29 * 60 + 30] },
  SBD3: { day: [7 * 60, 17 * 60 + 30], night: [18 * 60 + 30, 29 * 60] },
  SCK8: { day: [7 * 60, 17 * 60 + 30], night: [18 * 60, 28 * 60 + 30] },
};
const pad = n => String(n).padStart(2, '0');
const hm = m => `${pad(Math.floor(m / 60) % 24)}:${pad(m % 60)}`;
const toMin = s => { const [h, mm] = String(s).split(':').map(Number); return h * 60 + mm; };

function defaultShift(site) {
  const now = new Date();
  const nowM = now.getHours() * 60 + now.getMinutes();
  const w = SHIFTS[site] || SHIFTS.OLM1;
  const d = new Date(now);
  let shift = 'day';
  if (nowM < w.day[0]) { shift = 'night'; d.setDate(d.getDate() - 1); }
  else if (nowM >= w.night[0] - 15) shift = 'night';
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, shift };
}
// Shift-relative minute index for an HH:MM key (night wraps past midnight).
function relIndex(key, start) {
  let m = toMin(key);
  if (m < start - 6 * 60) m += 24 * 60;
  return m - start;
}

export default function JamControl({ api, site, user }) {
  const init = useMemo(() => defaultShift(site), [site]);
  const [date, setDate] = useState(init.date);
  const [shift, setShift] = useState(init.shift);
  const [series, setSeries] = useState('pid');
  const [minutes, setMinutes] = useState(null);
  const [pidLines, setPidLines] = useState(null);
  const [events, setEvents] = useState([]);
  const [autoEvents, setAutoEvents] = useState([]);
  const [showCountOnly, setShowCountOnly] = useState(false);
  const [status, setStatus] = useState('');
  const [drag, setDrag] = useState(null);       // {a, b} shift-relative minutes
  const [draft, setDraft] = useState(null);     // {start, end, type, note}
  const [zoom, setZoom] = useState([0, 1]);     // fraction of shift shown
  const svgRef = useRef(null);

  const [winStart, winEnd] = (SHIFTS[site] || SHIFTS.OLM1)[shift];
  const span = winEnd - winStart;
  const flagKey = `JAMCTRL_${date}`;

  const load = useCallback(async () => {
    const get = (d, st) => fetch(`${api}/api/fclm/cache?site=${site}&date=${d}&shift_type=${st}`).then(r => (r.ok ? r.json() : null)).catch(() => null);
    const [mp, fl, pl, au] = await Promise.all([get(`MP_MINUTES_${date}`, shift), get(flagKey, shift), get(`MP_PIDLINES_${date}`, shift), get(`JAMCTRL_AUTO_${date}`, shift)]);
    setAutoEvents(au && Array.isArray(au.events) ? au.events : []);
    setPidLines(pl && pl.minutes ? pl : null);
    setMinutes(mp && mp.minutes ? mp.minutes : {});
    setEvents(fl && Array.isArray(fl.scans) ? fl.scans : []);
    setStatus(mp && mp.fetched ? `iGraph synced ${new Date(mp.fetched).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'No per-minute data for this shift yet');
  }, [api, site, date, shift, flagKey]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 60 * 1000); return () => clearInterval(t); }, [load]);

  // Per-minute values on the shift axis (null = no data -> gap).
  const values = useMemo(() => {
    const arr = Array(span).fill(null);
    for (const [k, v] of Object.entries(minutes || {})) {
      const i = relIndex(k, winStart);
      if (i < 0 || i >= span) continue;
      arr[i] = series === 'pid' ? (v.pid || 0) : series === 'aros' ? (v.aros || 0) : (v.pid || 0) + (v.aros || 0);
    }
    return arr;
  }, [minutes, series, span, winStart]);

  // Fold the append-only log into current flags (JamBot auto flags first, so an AM
  // tombstone in the log removes them too).
  const flags = useMemo(() => {
    const byId = new Map();
    for (const e of autoEvents) if (e && e.id && e.start && e.end && COLOR[e.type] && !AUTO_HIDDEN.has(e.type)) byId.set(e.id, { ...e, auto: true });
    for (const e of events) {
      if (e.op === 'add') byId.set(e.id, e);
      else if (e.op === 'remove') byId.delete(e.id);
    }
    return [...byId.values()].sort((a, b) => relIndex(a.start, winStart) - relIndex(b.start, winStart));
  }, [events, autoEvents, winStart]);

  const stats = useMemo(() => {
    // Chart-or-count-only for auto flags (AM flags are always on the chart).
    const anyFlag = new Set();
    for (const f of flags) for (let i = relIndex(f.start, winStart); i <= relIndex(f.end, winStart); i++) anyFlag.add(i);
    const judged = flags.map(f => {
      if (!f.auto) return { ...f, view: 'chart' };
      const a = relIndex(f.start, winStart), b = Math.max(relIndex(f.end, winStart), a + DIP_MIN_WINDOW - 1);
      const win = []; for (let i = a; i <= b; i++) win.push(i >= 0 && i < span ? values[i] : null);
      if (win.some(v => v == null)) return { ...f, view: 'pending' };   // per-minute data not in yet: show it
      const h0 = Math.floor(Math.max(0, a) / 60) * 60;
      const clean = values.slice(h0, Math.min(span, h0 + 60)).filter((v, k) => v != null && v > 0 && !anyFlag.has(h0 + k));
      if (!clean.length) return { ...f, view: 'pending' };
      const hourAvg = clean.reduce((x, y) => x + y, 0) / clean.length;
      const winAvg = win.reduce((x, y) => x + y, 0) / win.length;
      return { ...f, view: winAvg < DIP_RATIO * hourAvg ? 'chart' : 'count', winAvg, hourAvg };
    });
    const onChart = judged.filter(f => f.view !== 'count');
    const flagged = new Set();
    for (const f of onChart) for (let i = relIndex(f.start, winStart); i <= relIndex(f.end, winStart); i++) flagged.add(i);
    const clean = values.map((v, i) => (v != null && !flagged.has(i) ? v : null)).filter(v => v != null);
    const baseline = clean.length ? clean.reduce((a, b) => a + b, 0) / clean.length : 0;
    const total = values.reduce((a, v) => a + (v || 0), 0);
    const rows = judged.map(f => {
      const a = relIndex(f.start, winStart), b = relIndex(f.end, winStart);
      const vals = values.slice(Math.max(0, a), Math.min(span, b + 1));
      const mins = b - a + 1;
      const actual = vals.reduce((s, v) => s + (v || 0), 0);
      const lost = f.view === 'count' ? 0 : Math.max(0, Math.round(baseline * mins - actual));
      return { ...f, mins, actual, avg: mins ? actual / mins : 0, lost };
    });
    const byType = FLAG_TYPES.map(t => {
      const r = rows.filter(x => x.type === t.key), c = r.filter(x => x.view !== 'count');
      return { type: t.key, count: r.length, shown: c.length, mins: c.reduce((s, x) => s + x.mins, 0), lost: c.reduce((s, x) => s + x.lost, 0) };
    });
    // Hourly cumulative production + flagged minutes per type in each hour.
    const hourly = [];
    let run = 0;
    for (let h0 = 0; h0 < span; h0 += 60) {
      const vals = values.slice(h0, Math.min(span, h0 + 60));
      const prod = vals.reduce((a, v) => a + (v || 0), 0); run += prod;
      // Lost Cartons = (avg/min of unflagged minutes - avg/min of flagged minutes)
      // x flagged minutes. Unflagged zero minutes (breaks, lunch, start/end of
      // shift) are left out of the unflagged average; flagged minutes count as-is.
      const normal = [], hit = [];
      vals.forEach((v, k) => {
        if (v == null) return;
        if (flagged.has(h0 + k)) hit.push(v); else if (v > 0) normal.push(v);
      });
      const avg = a => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
      const normalAvg = avg(normal), hitAvg = avg(hit);
      const lost = hit.length && normalAvg != null ? Math.max(0, Math.round((normalAvg - hitAvg) * hit.length)) : 0;
      const byT = {}, cntT = {};
      for (const f of rows) {
        const s0 = relIndex(f.start, winStart);
        if (s0 >= h0 && s0 < h0 + 60) cntT[f.type] = (cntT[f.type] || 0) + 1;   // every event, by start hour
        if (f.view === 'count') continue;
        const a = Math.max(h0, s0), b = Math.min(h0 + 59, relIndex(f.end, winStart));
        if (b >= a) byT[f.type] = (byT[f.type] || 0) + (b - a + 1);
      }
      hourly.push({ hour: hm(winStart + h0), prod, run, active: vals.filter(v => v != null).length, byT, cntT, lost, normalAvg, hitAvg, hitMins: hit.length });
    }
    return { baseline, total, rows, byType, flaggedMins: flagged.size, hourly, countOnly: rows.filter(r => r.view === 'count').length };
  }, [flags, values, span, winStart]);
  const listRows = showCountOnly ? stats.rows : stats.rows.filter(r => r.view !== 'count');

  const append = async (records) => {
    const res = await fetch(`${api}/api/fclm/scan-append`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: flagKey, shift_type: shift, scans: records }),
    });
    if (!res.ok) { setStatus('Save failed — try again'); return false; }
    await load();
    return true;
  };
  const saveDraft = async () => {
    const a = toMin(draft.start), b = toMin(draft.end);
    if (!draft.type || Number.isNaN(a) || Number.isNaN(b)) return;
    const [s, e] = relIndex(draft.start, winStart) <= relIndex(draft.end, winStart) ? [draft.start, draft.end] : [draft.end, draft.start];
    const ok = await append([{ op: 'add', id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, type: draft.type, start: s, end: e, note: (draft.note || '').slice(0, 300), by: user || 'unknown', at: new Date().toISOString() }]);
    if (ok) setDraft(null);
  };
  const removeFlag = f => {
    if (window.confirm(`Remove ${f.type} ${f.start}–${f.end}? (kept in history)`)) append([{ op: 'remove', id: f.id, by: user || 'unknown', at: new Date().toISOString() }]);
  };

  // Typed zoom times (HH:MM, night wraps past midnight). Clamped to the shift window and
  // kept at least 10 min apart, the same minimum span the chart enforces.
  const setZoomTime = (which, v) => {
    if (!/^\d{2}:\d{2}$/.test(v || '') || !(span > 0)) return; // incomplete entry: wait
    const i = Math.max(0, Math.min(span, relIndex(v, winStart)));
    const s0 = Math.floor(zoom[0] * span), s1 = Math.ceil(zoom[1] * span);
    if (which === 0) setZoom([(Math.min(i, s1 - 10) + 1e-6) / span, zoom[1]]);
    else setZoom([zoom[0], (Math.max(i, s0 + 10) - 1e-6) / span]);
  };
  // ── Chart geometry ──
  const W = 1150, H = 300, L = 44, R = 50, T = 16, B = 34;
  const z0 = Math.floor(zoom[0] * span), z1 = Math.max(z0 + 10, Math.ceil(zoom[1] * span));
  const x = i => L + ((i - z0) / (z1 - z0)) * (W - L - R);
  const maxV = Math.max(10, ...values.filter(v => v != null));
  const yMax = Math.ceil(maxV / 10) * 10;
  const y = v => T + (1 - v / yMax) * (H - T - B);
  const cum = []; values.reduce((s, v, i) => (cum[i] = s + (v || 0)), 0);
  const cumMax = Math.max(1, cum[cum.length - 1] || 1);
  const yc = v => T + (1 - v / cumMax) * (H - T - B);
  const segs = []; let cur = [];
  values.forEach((v, i) => { if (i < z0 || i > z1) return; if (v == null) { if (cur.length) segs.push(cur); cur = []; } else cur.push([x(i), y(v)]); });
  if (cur.length) segs.push(cur);
  const idxFromEvent = e => {
    const r = svgRef.current.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    return Math.round(z0 + Math.min(1, Math.max(0, (px - L) / (W - L - R))) * (z1 - z0));
  };
  const onDown = e => { const i = idxFromEvent(e); setDrag({ a: i, b: i }); };
  const onMove = e => { if (drag) setDrag({ ...drag, b: idxFromEvent(e) }); };
  const onUp = () => {
    if (!drag) return;
    const a = Math.min(drag.a, drag.b), b = Math.max(drag.a, drag.b);
    setDrag(null);
    setDraft({ start: hm(winStart + a), end: hm(winStart + b), type: draft?.type || 'Jams', note: '' });
  };
  const ticks = []; for (let m = Math.ceil((winStart + z0) / 30) * 30; m <= winStart + z1; m += 30) ticks.push(m - winStart);

  const card = { background: '#fff', border: '1px solid #E7E9E9', borderRadius: 8, padding: 16, marginBottom: 16 };
  const th = { textAlign: 'left', fontSize: 12, color: '#697576', padding: '6px 8px', borderBottom: '1px solid #E7E9E9' };
  const td = { fontSize: 13, padding: '6px 8px', borderBottom: '1px solid #E7E9E9', whiteSpace: 'nowrap' };

  return (
    <div>
      <h2 className="section-title">Jam Control Station — {site}</h2>
      <div style={{ ...card, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12 }}>Date<br /><input type="date" value={date} onChange={e => setDate(e.target.value)} /></label>
        <label style={{ fontSize: 12 }}>Shift<br /><select value={shift} onChange={e => setShift(e.target.value)}><option value="day">Day</option><option value="night">Night</option></select></label>
        <label style={{ fontSize: 12 }}>Production<br /><select value={series} onChange={e => setSeries(e.target.value)}>{Object.entries(SERIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <button className="btn" onClick={() => setDraft({ start: hm(winStart), end: hm(winStart + 4), type: 'Jams', note: '' })}>+ Add flag</button>
        <span style={{ fontSize: 12, color: '#697576' }}>{status} · drag across the chart to flag a range</span>
      </div>

      <div style={card}>
        <h3 style={{ textAlign: 'center', margin: '0 0 8px', fontSize: 18 }}>Cartons Created — {SERIES[series]} per minute</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
          Zoom
          <input type="time" step="60" value={hm(winStart + z0)} onChange={e => setZoomTime(0, e.target.value)} aria-label="Zoom start time" style={{ fontSize: 12 }} />
          <input type="range" min="0" max="0.95" step="0.01" value={zoom[0]} onChange={e => setZoom([Math.min(Number(e.target.value), zoom[1] - 0.05), zoom[1]])} aria-label="Zoom start" />
          <input type="range" min="0.05" max="1" step="0.01" value={zoom[1]} onChange={e => setZoom([zoom[0], Math.max(Number(e.target.value), zoom[0] + 0.05)])} aria-label="Zoom end" />
          <input type="time" step="60" value={hm(winStart + z1)} onChange={e => setZoomTime(1, e.target.value)} aria-label="Zoom end time" style={{ fontSize: 12 }} />
          <button className="btn" onClick={() => setZoom([0, 1])}>Reset</button>
        </div>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', userSelect: 'none', cursor: 'crosshair' }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={() => drag && onUp()} role="img"
          aria-label={`Per-minute ${SERIES[series]} production for ${date} ${shift} shift`}>
          {stats.rows.filter(f => f.view !== 'count').map(f => {
            const a = Math.max(z0, relIndex(f.start, winStart)), b = Math.min(z1, relIndex(f.end, winStart));
            if (b < z0 || a > z1) return null;
            return <rect key={f.id} x={x(a) - 2} width={Math.max(4, x(b) - x(a) + 4)} y={T} height={H - T - B} fill={COLOR[f.type]} opacity="0.18"><title>{`${f.type} ${f.start}–${f.end} (${f.by})${f.note ? ` — ${f.note}` : ''}`}</title></rect>;
          })}
          {[0, 0.5, 1].map(p => <g key={p}><line x1={L} x2={W - R} y1={y(yMax * p)} y2={y(yMax * p)} stroke="#BBC0C1" strokeWidth="1" /><text x={L - 6} y={y(yMax * p) + 4} fontSize="12" textAnchor="end">{Math.round(yMax * p)}</text><text x={W - R + 6} y={y(yMax * p) + 4} fontSize="12">{Math.round(cumMax * p)}</text></g>)}
          {ticks.map(t => <g key={t}><line x1={x(t)} x2={x(t)} y1={T} y2={H - B} stroke="#E7E9E9" /><text x={x(t)} y={H - B + 18} fontSize="12" textAnchor="middle">{hm(winStart + t)}</text></g>)}
          <polyline fill="none" stroke="#BBC0C1" strokeDasharray="4 3" strokeWidth="1.5" points={cum.map((c, i) => (i >= z0 && i <= z1 ? `${x(i)},${yc(c)}` : null)).filter(Boolean).join(' ')} />
          {segs.map((s, k) => <polyline key={k} fill="none" stroke="#2DD4ED" strokeWidth="1.5" points={s.map(p => p.join(',')).join(' ')} />)}
          {(z1 - z0) <= 240 && values.map((v, i) => (v != null && i >= z0 && i <= z1 ? <circle key={i} cx={x(i)} cy={y(v)} r="3" fill="#2DD4ED"><title>{`${hm(winStart + i)}: ${v}`}</title></circle> : null))}
          {drag && <rect x={x(Math.min(drag.a, drag.b))} width={Math.max(2, Math.abs(x(drag.b) - x(drag.a)))} y={T} height={H - T - B} fill="#077398" opacity="0.15" />}
        </svg>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, marginTop: 4 }}>
          {FLAG_TYPES.map(t => <span key={t.key}><span style={{ display: 'inline-block', width: 12, height: 12, background: t.color, opacity: 0.5, marginRight: 4, verticalAlign: 'middle' }} />{t.key}</span>)}
          <span style={{ color: '#697576' }}>Dashed line = cumulative (right axis)</span>
        </div>
      </div>

      {draft && (
        <div style={{ ...card, borderColor: '#077398' }}>
          <strong style={{ fontSize: 14 }}>New flag</strong>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 8 }}>
            <label style={{ fontSize: 12 }}>Type<br /><select value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })}>{FLAG_TYPES.map(t => <option key={t.key}>{t.key}</option>)}</select></label>
            <label style={{ fontSize: 12 }}>From<br /><input type="time" value={draft.start} onChange={e => setDraft({ ...draft, start: e.target.value })} /></label>
            <label style={{ fontSize: 12 }}>To<br /><input type="time" value={draft.end} onChange={e => setDraft({ ...draft, end: e.target.value })} /></label>
            <label style={{ fontSize: 12, flex: 1, minWidth: 220 }}>Note<br /><input value={draft.note} maxLength={300} onChange={e => setDraft({ ...draft, note: e.target.value })} style={{ width: '100%' }} placeholder="e.g. jam at PID line 2" /></label>
            <button className="btn btn-primary" onClick={saveDraft}>Save</button>
            <button className="btn" onClick={() => setDraft(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={card}>
        <strong style={{ fontSize: 14 }}>Cumulative production by hour — {SERIES[series]}</strong>
        <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
          <thead><tr>{['Hour', 'Cartons', 'Cumulative', 'Minutes with data', 'Avg/min', ...FLAG_TYPES.map(t => `${t.key} min (#)`), 'Lost Cartons'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {stats.hourly.map(r => (
              <tr key={r.hour}>
                <td style={td}>{r.hour}</td><td style={td}>{Math.round(r.prod).toLocaleString()}</td><td style={td}>{Math.round(r.run).toLocaleString()}</td>
                <td style={td}>{r.active}</td><td style={td}>{r.active ? (r.prod / r.active).toFixed(1) : '—'}</td>
                {FLAG_TYPES.map(t => <td key={t.key} style={{ ...td, color: r.byT[t.key] ? COLOR[t.key] : '#BBC0C1', fontWeight: r.byT[t.key] ? 700 : 400 }}
                  title={r.cntT[t.key] ? `${r.cntT[t.key]} ${t.key} event(s) started this hour; minutes count only those that dipped production` : ''}>
                  {r.byT[t.key] || '—'}{r.cntT[t.key] ? <span style={{ color: '#697576', fontWeight: 400 }}> ({r.cntT[t.key]})</span> : null}</td>)}
                <td style={{ ...td, fontWeight: r.lost ? 700 : 400, color: r.lost ? '#DE1B1B' : '#BBC0C1' }}
                  title={r.hitMins ? `(${r.normalAvg != null ? r.normalAvg.toFixed(1) : '—'} normal − ${r.hitAvg.toFixed(1)} flagged) × ${r.hitMins} flagged min` : ''}>
                  {r.lost ? r.lost.toLocaleString() : '—'}
                </td>
              </tr>
            ))}
            <tr style={{ fontWeight: 700 }}>
              <td style={td}>Total</td><td style={td}>{Math.round(stats.total).toLocaleString()}</td><td style={td} /><td style={td}>{stats.hourly.reduce((s, r) => s + r.active, 0)}</td><td style={td} />
              {FLAG_TYPES.map(t => { const n = stats.hourly.reduce((s, r) => s + (r.cntT[t.key] || 0), 0); return <td key={t.key} style={td}>{stats.hourly.reduce((s, r) => s + (r.byT[t.key] || 0), 0) || '—'}{n ? <span style={{ color: '#697576', fontWeight: 400 }}> ({n})</span> : null}</td>; })}
              <td style={{ ...td, color: '#DE1B1B' }}>{stats.hourly.reduce((s, r) => s + r.lost, 0).toLocaleString() || '—'}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ ...card, flex: '0 0 auto' }}>
          <strong style={{ fontSize: 14 }}>Shift summary</strong>
          <table style={{ borderCollapse: 'collapse', marginTop: 8 }}>
            <thead><tr><th style={th}>Type</th><th style={th}>Flags</th><th style={th}>On chart</th><th style={th}>Minutes</th><th style={th}>Est. cartons lost</th></tr></thead>
            <tbody>
              {stats.byType.map(r => <tr key={r.type}><td style={td}><span style={{ color: COLOR[r.type], fontWeight: 700 }}>■</span> {r.type}</td><td style={td}>{r.count}</td><td style={td}>{r.shown}</td><td style={td}>{r.mins}</td><td style={td}>{r.lost.toLocaleString()}</td></tr>)}
              <tr style={{ fontWeight: 700 }}><td style={td}>Total</td><td style={td}>{stats.rows.length}</td><td style={td}>{stats.rows.length - stats.countOnly}</td><td style={td}>{stats.flaggedMins}</td><td style={td}>{stats.byType.reduce((s, r) => s + r.lost, 0).toLocaleString()}</td></tr>
            </tbody>
          </table>
          <div style={{ fontSize: 12, color: '#697576', marginTop: 8 }}>
            Shift total {Math.round(stats.total).toLocaleString()} {SERIES[series]} · unflagged baseline {stats.baseline.toFixed(1)}/min.<br />
            Est. lost = baseline × minutes − actual in the range.<br />
            JamBot flags (SCADA) are drawn only when production dipped below half the hour's average; the rest are count only. JamBot AGL (full beds) are on the Sorter Map tab.
          </div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 600 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>Flags</strong>
            {stats.countOnly > 0 && <label style={{ fontSize: 12 }}><input type="checkbox" checked={showCountOnly} onChange={e => setShowCountOnly(e.target.checked)} /> Show {stats.countOnly} count-only JamBot event{stats.countOnly === 1 ? '' : 's'}</label>}
          </div>
          <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
            <thead><tr>{['Type', 'From', 'To', 'Minutes', 'Actual', 'Avg/min', 'Est. lost', 'Chart', 'Flagged by', 'Note', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {listRows.length === 0 && <tr><td style={{ ...td, color: '#697576' }} colSpan={11}>No flags yet — drag across the chart or use “Add flag”.</td></tr>}
              {listRows.map(f => (
                <tr key={f.id} style={f.view === 'count' ? { color: '#697576' } : undefined}>
                  <td style={td}><span style={{ color: COLOR[f.type], fontWeight: 700 }}>■</span> {f.type}</td>
                  <td style={td}>{f.start}</td><td style={td}>{f.end}{f.active ? <span style={{ color: '#DE1B1B', fontWeight: 700 }}> · active</span> : null}</td><td style={td}>{f.mins}</td>
                  <td style={td}>{Math.round(f.actual)}</td><td style={td}>{f.avg.toFixed(1)}</td><td style={td}>{f.lost}</td>
                  <td style={td} title={f.hourAvg != null ? `${f.winAvg.toFixed(1)}/min vs hour ${f.hourAvg.toFixed(1)}/min` : ''}>{f.view === 'count' ? 'Count only' : f.view === 'pending' ? 'Pending data' : 'Yes'}</td>
                  <td style={td}>{f.by}</td><td style={{ ...td, whiteSpace: 'normal', maxWidth: 260 }}>{f.note}</td>
                  <td style={td}><button className="btn" onClick={() => removeFlag(f)} aria-label={`Remove ${f.type} ${f.start} to ${f.end}`}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {[1, 2].map(p => (
        <PidGraph key={p} pid={p} site={site} data={pidLines} winStart={winStart} span={span} z0={z0} z1={z1} />
      ))}
    </div>
  );
}

// Informational PID-1 / PID-2 encounter outcomes (MonitorPortal PerformanceHealthHandler).
// No flagging. Shift view follows the main chart's zoom; "Last hour" always shows the
// 60 minutes up to the newest data point (re-renders on the page's 1-minute refresh).
// Y axis is labelled at hourly pace (cartons/min x 60), like the iGraph Cartons/Hour board.
const PID_SERIES = [
  { k: 'r', label: 'RECEIVED', color: '#47B120' },
  { k: 'cci', label: 'CANNOT_CHECK_IN', color: '#FF8F00' },
  { k: 'cr', label: 'CANNOT_RECEIVE', color: '#DE1B1B' },
];
const GOLD_LINE = '#E3CD8B', GOLD_DOT = '#F2C23E', GRID = '#E7E9E9';
function PidGraph({ pid, site, data, winStart, span, z0, z1 }) {
  const boxRef = useRef(null);
  const [full, setFull] = useState(false);
  const [mode, setMode] = useState('shift');          // 'shift' | 'hour'
  const [breakdown, setBreakdown] = useState(false);
  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFull = () => {
    const el = boxRef.current;
    if (!full) { setFull(true); if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {}); }
    else { setFull(false); if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {}); }
  };
  const series = useMemo(() => {
    const out = { r: Array(span).fill(null), cci: Array(span).fill(null), cr: Array(span).fill(null), sum: Array(span).fill(null) };
    for (const [k, v] of Object.entries((data && data.minutes) || {})) {
      const i = relIndex(k, winStart);
      if (i < 0 || i >= span) continue;
      const r = v[`p${pid}r`], cci = v[`p${pid}cci`], cr = v[`p${pid}cr`];
      if (r == null && cci == null && cr == null) continue;
      out.r[i] = r || 0; out.cci[i] = cci || 0; out.cr[i] = cr || 0; out.sum[i] = (r || 0) + (cci || 0) + (cr || 0);
    }
    return out;
  }, [data, pid, span, winStart]);
  // View window.
  let lastIdx = -1;
  series.sum.forEach((v, i) => { if (v != null) lastIdx = i; });
  const [v0, v1] = mode === 'hour' && lastIdx >= 0 ? [Math.max(0, lastIdx - 59), Math.max(lastIdx, 59)] : [z0, z1];
  const inView = series.sum.slice(v0, v1 + 1).filter(v => v != null);
  const total = inView.reduce((a, b) => a + b, 0);
  const avg = inView.length ? total / inView.length : null;
  // Geometry: y in cartons/min, labelled x60 at "nice" hourly steps.
  const W = 1150, H = full ? 560 : 260, L = 56, R = 20, T = 14, B = 32;
  const x = i => L + ((i - v0) / Math.max(1, v1 - v0)) * (W - L - R);
  const maxHr = Math.max(60, ...inView.map(v => v * 60));
  const step = [60, 120, 300, 600, 1200, 3000].find(st => maxHr / st <= 5) || 6000;
  const topHr = Math.ceil(maxHr / step) * step;
  const yMax = topHr / 60;
  const y = v => T + (1 - v / yMax) * (H - T - B);
  const yTicks = []; for (let hr = 0; hr <= topHr; hr += step) yTicks.push(hr);
  const tickEvery = mode === 'hour' ? 5 : 30;
  const xTicks = []; for (let m = Math.ceil((winStart + v0) / tickEvery) * tickEvery; m <= winStart + v1; m += tickEvery) xTicks.push(m - winStart);
  const line = arr => {
    const segs = []; let cur = [];
    arr.forEach((v, i) => { if (i < v0 || i > v1) return; if (v == null) { if (cur.length) segs.push(cur); cur = []; } else cur.push(`${x(i)},${y(v)}`); });
    if (cur.length) segs.push(cur);
    return segs;
  };
  const showDots = v1 - v0 <= 240;
  const boxStyle = full
    ? { position: 'fixed', inset: 0, zIndex: 2000, background: '#fff', padding: 24, overflow: 'auto' }
    : { background: '#fff', border: '1px solid #E7E9E9', borderRadius: 12, padding: 16, marginBottom: 16 };
  const seg = active => ({ padding: '4px 12px', border: '1px solid #BBC0C1', background: active ? '#232F3E' : '#fff', color: active ? '#fff' : '#232F3E', cursor: 'pointer', fontSize: 12 });
  return (
    <div ref={boxRef} style={boxStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{site} PID-{pid} — Cartons Per Hour:</div>
          <div style={{ fontSize: 26, fontWeight: 800, marginLeft: 40 }}>{avg != null ? Math.round(avg * 60).toLocaleString() : '—'}</div>
          <div style={{ fontSize: 12, color: '#697576' }}>
            {mode === 'hour' ? 'Last 60 minutes' : 'Shift view'}: total {total.toLocaleString()} · avg {avg != null ? avg.toFixed(1) : '—'}/min · {inView.length} min with data
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div role="group" aria-label={`PID-${pid} time range`} style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden' }}>
            <button type="button" style={{ ...seg(mode === 'shift'), borderRadius: '6px 0 0 6px' }} aria-pressed={mode === 'shift'} onClick={() => setMode('shift')}>Shift</button>
            <button type="button" style={{ ...seg(mode === 'hour'), borderRadius: '0 6px 6px 0', borderLeft: 'none' }} aria-pressed={mode === 'hour'} onClick={() => setMode('hour')}>Last hour</button>
          </div>
          <label style={{ fontSize: 12 }}><input type="checkbox" checked={breakdown} onChange={e => setBreakdown(e.target.checked)} /> Show breakdown</label>
          <button className="btn" onClick={toggleFull} aria-label={full ? `Exit full screen PID-${pid}` : `Full screen PID-${pid}`}>{full ? '✕ Exit full screen' : '⛶ Full screen'}</button>
        </div>
      </div>
      {!series.sum.some(v => v != null) ? <p style={{ fontSize: 13, color: '#697576' }}>No PID-{pid} data for this shift yet.</p> : (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: full ? 'calc(100vh - 150px)' : 'auto' }} role="img"
          aria-label={`PID-${pid} cartons per hour pace, ${mode === 'hour' ? 'last 60 minutes' : 'shift'}`} preserveAspectRatio={full ? 'none' : 'xMidYMid meet'}>
          {yTicks.map(hr => <g key={hr}><line x1={L} x2={W - R} y1={y(hr / 60)} y2={y(hr / 60)} stroke={GRID} /><text x={L - 6} y={y(hr / 60) + 4} fontSize="12" fontWeight="700" textAnchor="end">{hr.toLocaleString()}</text></g>)}
          <text x={14} y={(H - B + T) / 2} fontSize="11" textAnchor="middle" transform={`rotate(-90 14 ${(H - B + T) / 2})`}>Cartons/Hour</text>
          {xTicks.map(t => <g key={t}><line x1={x(t)} x2={x(t)} y1={T} y2={H - B} stroke={GRID} /><text x={x(t)} y={H - B + 18} fontSize="12" fontWeight="700" textAnchor="middle">{hm(winStart + t)}</text></g>)}
          {breakdown && PID_SERIES.map(sv => line(series[sv.k]).map((sg, k) => <polyline key={`${sv.k}${k}`} fill="none" stroke={sv.color} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" opacity="0.7" points={sg.join(' ')} />))}
          {line(series.sum).map((sg, k) => <polyline key={`s${k}`} fill="none" stroke={GOLD_LINE} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" points={sg.join(' ')} />)}
          {showDots && series.sum.map((v, i) => (v != null && i >= v0 && i <= v1
            ? <circle key={i} cx={x(i)} cy={y(v)} r="3.5" fill="#fff" stroke={GOLD_DOT} strokeWidth="2"><title>{`${hm(winStart + i)}: ${v}/min (${v * 60}/hr pace)`}</title></circle> : null))}
        </svg>
      )}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, marginTop: 4 }}>
        <span><span style={{ display: 'inline-block', width: 14, height: 3, background: GOLD_LINE, marginRight: 4, verticalAlign: 'middle' }} />SUM (received + cannot check in + cannot receive)</span>
        {breakdown && PID_SERIES.map(sv => <span key={sv.k}><span style={{ display: 'inline-block', width: 14, height: 3, background: sv.color, marginRight: 4, verticalAlign: 'middle' }} />{sv.label}</span>)}
        <span style={{ color: '#697576' }}>Y axis = per-minute pace × 60. {mode === 'shift' ? 'Follows the zoom above.' : 'Updates every minute.'}</span>
      </div>
    </div>
  );
}
